/**
 * Auto-add gift products so Shopify can discount them and deduct inventory.
 *
 * Watches:
 *   normal watch -> B2 Leather Box + Authenticity Card + Cleaning Cloth
 *   ACE watch    -> ACEBOX-1 + Authenticity Card + Cleaning Cloth
 *
 * Straps (product_type === "Strap"):
 *   1 strap -> 1 Strap Box + 1 Strap Tool
 *   Discount codes FREE STRAP BOX and FREE STRAP TOOL are auto-applied because
 *   Shopify Buy X Get Y only frees one gift when both discounts share the same strap.
 *
 * B2 Leather Box uses discount code FREE B2 BOX (auto-applied only while a normal
 * watch is in the cart). That keeps a separately purchased box at full price —
 * an automatic amount-off with no watch requirement would free every B2 box.
 * ACE Box stays on Shopify Buy X Get Y (requires an ACE watch).
 *
 * Only line items with _auto_watch_gift = true are managed here.
 * Manually added gift products (B2 box, Strap Box, Strap Tool) are left untouched
 * for quantity sync; they are only free if a matching gift discount code/BXGY applies.
 */
(() => {
  const GIFT_VARIANTS = {
    aceBox: 57237712732486,
    b2Box: 57232563077446,
    authenticityCard: 57232787407174,
    cleaningCloth: 57590671606086,
    strapBox: 33144393040003,
    strapTool: 57313837252934
  };

  const GIFT_TYPES = {
    aceBox: 'ace-box',
    b2Box: 'b2-box',
    authCard: 'auth-card',
    cleaningCloth: 'cleaning-cloth',
    strapBox: 'strap-box',
    strapTool: 'strap-tool'
  };

  const AUTO_GIFT_PROP = '_auto_watch_gift';
  const GIFT_TYPE_PROP = '_watch_gift_type';
  const REQUEST_HEADER = 'X-Watch-Gifts';
  const PROCEED_ATTR = 'data-watch-gifts-proceed';
  const B2_BOX_DISCOUNT_CODE = 'FREE B2 BOX';
  const STRAP_GIFT_DISCOUNT_CODES = ['FREE STRAP BOX', 'FREE STRAP TOOL'];
  const MANAGED_DISCOUNT_CODES = [B2_BOX_DISCOUNT_CODE, ...STRAP_GIFT_DISCOUNT_CODES];

  const GIFT_PLANS = [
    { key: 'b2Box', type: GIFT_TYPES.b2Box, variantId: GIFT_VARIANTS.b2Box },
    { key: 'aceBox', type: GIFT_TYPES.aceBox, variantId: GIFT_VARIANTS.aceBox },
    { key: 'authCard', type: GIFT_TYPES.authCard, variantId: GIFT_VARIANTS.authenticityCard },
    { key: 'cleaningCloth', type: GIFT_TYPES.cleaningCloth, variantId: GIFT_VARIANTS.cleaningCloth },
    { key: 'strapBox', type: GIFT_TYPES.strapBox, variantId: GIFT_VARIANTS.strapBox },
    { key: 'strapTool', type: GIFT_TYPES.strapTool, variantId: GIFT_VARIANTS.strapTool }
  ];

  const GIFT_VARIANT_IDS = new Set(GIFT_PLANS.map((gift) => Number(gift.variantId)));
  const nativeFetch = window.fetch.bind(window);

  let syncing = false;
  let queued = false;
  let hasSyncedOnce = false;
  let debounceTimer = 0;
  let pendingRun = null;
  let pendingResolve = null;
  let loadingDepth = 0;
  let checkoutHoldCount = 0;
  let queuedCart = null;
  let lastSyncedCart = null;

  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
  const getProperties = (item) => (item?.properties && typeof item.properties === 'object' ? item.properties : {});
  const isAutoGift = (item) => String(getProperties(item)[AUTO_GIFT_PROP] || '') === 'true';
  const variantIdOf = (item) => Number(item?.variant_id || item?.id || 0);
  const isGiftVariant = (item) => GIFT_VARIANT_IDS.has(variantIdOf(item));

  const isWatch = (item) => {
    if (!item || isAutoGift(item) || isGiftVariant(item)) return false;
    return String(item.product_type || '').toLowerCase() === 'watch';
  };

  const isAceWatch = (item) => isWatch(item) && String(item.product_title || '').toLowerCase().includes('ace');

  const itemTags = (item) => {
    const tags = item?.tags;
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).toLowerCase());
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim().toLowerCase()).filter(Boolean);
    return [];
  };

  const isStrap = (item) => {
    if (!item || isAutoGift(item) || isGiftVariant(item)) return false;
    if (String(item.product_type || '').toLowerCase() !== 'strap') return false;
    return !itemTags(item).includes('no back-in-stock');
  };

  const syncIsPending = () => !hasSyncedOnce || syncing || queued || Boolean(pendingRun);

  const isWatchGiftsRequest = (init = {}) => {
    const headers = init.headers;
    if (!headers) return false;
    if (typeof headers.get === 'function') return headers.get(REQUEST_HEADER) === '1';
    if (Array.isArray(headers)) {
      return headers.some(([name, value]) => String(name).toLowerCase() === REQUEST_HEADER.toLowerCase() && value === '1');
    }
    return headers[REQUEST_HEADER] === '1' || headers[REQUEST_HEADER.toLowerCase()] === '1';
  };

  const cartPathname = (input) => {
    try {
      const url = typeof input === 'string' ? input : input?.url;
      return url ? new URL(url, window.location.origin).pathname : '';
    } catch (error) {
      return '';
    }
  };

  const shouldSyncAfterFetch = (input, init = {}) => {
    if (isWatchGiftsRequest(init)) return false;
    const pathname = cartPathname(input);
    if (/\/cart\/(add|change)\.js$/i.test(pathname)) return true;
    if (!/\/cart\/update\.js$/i.test(pathname)) return false;

    const body = typeof init.body === 'string' ? init.body : '';
    if (!body) return false;
    try {
      const payload = JSON.parse(body);
      return Boolean(payload.updates || payload.id || payload.quantity === 0);
    } catch (error) {
      return false;
    }
  };

  const sectionsFromInit = (init = {}) => {
    try {
      if (typeof init.body === 'string') {
        return JSON.parse(init.body).sections || '';
      }
      if (init.body instanceof FormData) {
        return init.body.get('sections') || '';
      }
    } catch (error) {
      return '';
    }
    return '';
  };

  const isFullCart = (payload) => (
    payload
    && Array.isArray(payload.items)
    && typeof payload.item_count === 'number'
  );

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return sections.filter(Boolean).join(',');
  };

  const setLoading = (isLoading) => {
    if (isLoading) {
      loadingDepth += 1;
      if (loadingDepth === 1) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      }
      return;
    }

    loadingDepth = Math.max(0, loadingDepth - 1);
    if (loadingDepth === 0) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  };

  const getCart = async () => {
    const response = await nativeFetch(`${cartRoot()}cart.js?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { [REQUEST_HEADER]: '1' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const postCart = async (path, body) => {
    const response = await nativeFetch(`${cartRoot()}${path}`, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        [REQUEST_HEADER]: '1'
      },
      body: JSON.stringify(body)
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(payload.description || payload.message || `Cart request failed: ${path}`);
      error.payload = payload;
      throw error;
    }
    return payload;
  };

  const desiredGiftQuantities = (cart) => {
    let normalWatchQty = 0;
    let aceWatchQty = 0;
    let strapQty = 0;

    for (const item of cart.items || []) {
      if (isStrap(item)) {
        strapQty += Number(item.quantity) || 0;
        continue;
      }
      if (!isWatch(item)) continue;
      if (isAceWatch(item)) aceWatchQty += Number(item.quantity) || 0;
      else normalWatchQty += Number(item.quantity) || 0;
    }

    return {
      b2Box: normalWatchQty,
      aceBox: aceWatchQty,
      authCard: normalWatchQty + aceWatchQty,
      cleaningCloth: normalWatchQty + aceWatchQty,
      strapBox: strapQty,
      strapTool: strapQty
    };
  };

  const autoGiftLinesFor = (cart, variantId) => {
    return (cart.items || []).filter((item) => isAutoGift(item) && variantIdOf(item) === Number(variantId));
  };

  const addGiftItems = async (items, sections) => {
    if (!items.length) return null;

    const body = { items };
    if (sections) body.sections = sections;

    try {
      return await postCart('cart/add.js', body);
    } catch (error) {
      let lastPayload = null;
      for (const item of items) {
        try {
          lastPayload = await postCart('cart/add.js', { items: [item], ...(sections ? { sections } : {}) });
        } catch (itemError) {
          console.warn('[watch-gifts] Could not add gift variant', item.id, itemError);
        }
      }
      return lastPayload;
    }
  };

  const applyDiff = async (cart, sections) => {
    const desired = desiredGiftQuantities(cart);
    const updates = {};
    const itemsToAdd = [];

    for (const gift of GIFT_PLANS) {
      const quantity = Number(desired[gift.key]) || 0;
      const lines = autoGiftLinesFor(cart, gift.variantId);
      const currentQty = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

      if (currentQty === quantity) continue;

      if (currentQty < quantity) {
        const delta = quantity - currentQty;
        if (lines[0]) {
          updates[lines[0].key] = (Number(lines[0].quantity) || 0) + delta;
        } else {
          itemsToAdd.push({
            id: gift.variantId,
            quantity: delta,
            properties: {
              [AUTO_GIFT_PROP]: 'true',
              [GIFT_TYPE_PROP]: gift.type
            }
          });
        }
        continue;
      }

      let remaining = currentQty - quantity;
      for (let index = lines.length - 1; index >= 0 && remaining > 0; index -= 1) {
        const line = lines[index];
        const lineQty = Number(line.quantity) || 0;
        if (lineQty <= remaining) {
          updates[line.key] = 0;
          remaining -= lineQty;
        } else {
          updates[line.key] = lineQty - remaining;
          remaining = 0;
        }
      }
    }

    let mutated = false;
    let resultCart = null;
    const sectionPayload = sections ? { sections } : {};

    if (Object.keys(updates).length) {
      resultCart = await postCart('cart/update.js', { updates, ...sectionPayload });
      mutated = true;
    }

    if (itemsToAdd.length) {
      const added = await addGiftItems(itemsToAdd, sections);
      mutated = true;
      if (isFullCart(added)) {
        resultCart = added;
      } else {
        const freshCart = await getCart();
        if (added?.sections) freshCart.sections = added.sections;
        resultCart = freshCart;
      }
    }

    if (mutated && resultCart && sections && !resultCart.sections) {
      resultCart = await postCart('cart/update.js', { updates: {}, sections });
    }

    const cartForDiscounts = resultCart || cart;
    const discountResult = await syncGiftDiscountCodes(cartForDiscounts, sections);
    if (discountResult.mutated) {
      mutated = true;
      resultCart = discountResult.cart || resultCart;
    }

    return { mutated, cart: resultCart };
  };

  const cartDiscountCodes = (cart) => {
    return (cart.discount_codes || [])
      .map((entry) => String(entry?.code || entry || '').trim())
      .filter(Boolean);
  };

  const isManagedGiftCode = (code) => {
    const normalized = String(code || '').trim().toUpperCase();
    return MANAGED_DISCOUNT_CODES.some((giftCode) => giftCode.toUpperCase() === normalized);
  };

  const requiredGiftDiscountCodes = (cart) => {
    const desired = desiredGiftQuantities(cart);
    const codes = [];
    // Only free the Leather Box while a normal watch is present — never when
    // the box is bought alone or left in the cart after the watch is removed.
    if (desired.b2Box > 0) codes.push(B2_BOX_DISCOUNT_CODE);
    if (desired.strapBox > 0) codes.push(...STRAP_GIFT_DISCOUNT_CODES);
    return codes;
  };

  const syncGiftDiscountCodes = async (cart, sections) => {
    if (!cart) return { mutated: false, cart };

    const currentCodes = cartDiscountCodes(cart);
    const customerCodes = currentCodes.filter((code) => !isManagedGiftCode(code));
    const nextCodes = [...customerCodes, ...requiredGiftDiscountCodes(cart)];

    const sameCodes = currentCodes.map((code) => code.toUpperCase()).sort().join(',')
      === nextCodes.map((code) => code.toUpperCase()).sort().join(',');
    if (sameCodes) return { mutated: false, cart };

    try {
      const updatedCart = await postCart('cart/update.js', {
        discount: nextCodes.join(','),
        ...(sections ? { sections } : {})
      });
      return { mutated: true, cart: updatedCart };
    } catch (error) {
      console.warn('[watch-gifts] Could not apply gift discount codes', error);
      return { mutated: false, cart };
    }
  };

  const publishSyncedCart = (cart) => {
    if (!cart) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'watch-gifts:sync',
        watchGiftsSynced: true,
        cart
      }
    }));
  };

  const finishPending = () => {
    const resolve = pendingResolve;
    pendingRun = null;
    pendingResolve = null;
    if (resolve) resolve(lastSyncedCart);
  };

  const runSync = async (options = {}) => {
    if (syncing) {
      queued = true;
      if (options.cart) queuedCart = options.cart;
      return pendingRun;
    }

    syncing = true;
    queued = false;
    let mutated = false;
    let resultCart = options.cart || null;
    const sections = options.sections || bundledSections();
    if (!options.silent) setLoading(true);

    try {
      do {
        queued = false;
        const cart = queuedCart || resultCart || await getCart();
        queuedCart = null;
        const result = await applyDiff(cart, sections);
        mutated = result.mutated || mutated;
        if (result.cart) resultCart = result.cart;
        else if (!resultCart) resultCart = cart;
      } while (queued);

      hasSyncedOnce = true;
      lastSyncedCart = resultCart;

      if (mutated && isCartPage() && !options.fromIntercept && checkoutHoldCount === 0) {
        window.location.reload();
        return lastSyncedCart;
      }

      if (mutated && !options.fromIntercept) publishSyncedCart(resultCart);
    } catch (error) {
      console.warn('[watch-gifts] Sync failed', error);
      hasSyncedOnce = true;
    } finally {
      syncing = false;
      if (!options.silent) setLoading(false);
      if (queued) scheduleSync();
      else finishPending();
    }

    return lastSyncedCart;
  };

  const scheduleSync = (cart) => {
    if (cart && isFullCart(cart)) queuedCart = cart;

    if (!pendingRun) {
      pendingRun = new Promise((resolve) => {
        pendingResolve = resolve;
      });
    }

    if (syncing) {
      queued = true;
      return pendingRun;
    }

    queued = true;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(runSync, 0);
    return pendingRun;
  };

  const checkoutTriggerFrom = (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target || typeof target.closest !== 'function') return null;

    return target.closest(
      'button[name="checkout"], input[name="checkout"], .shopify-payment-button button, .additional-checkout-buttons button, shopify-accelerated-checkout, a[href*="/checkout"]'
    );
  };

  const replayCheckout = (trigger) => {
    trigger.setAttribute(PROCEED_ATTR, '');
    if (typeof trigger.click === 'function') trigger.click();
    trigger.removeAttribute(PROCEED_ATTR);
  };

  const holdCheckoutIfNeeded = (event, trigger) => {
    if (!trigger || trigger.hasAttribute(PROCEED_ATTR) || !syncIsPending()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    trigger.setAttribute('aria-busy', 'true');
    checkoutHoldCount += 1;

    scheduleSync()
      .finally(() => {
        checkoutHoldCount = Math.max(0, checkoutHoldCount - 1);
        trigger.removeAttribute('aria-busy');
      })
      .then(() => replayCheckout(trigger));
  };

  const wrapUsfSuccess = () => {
    const current = window._usfOnAddToCartSuccess;
    if (typeof current === 'function' && current.__watchGiftsWrapped) return;

    const original = current;
    const wrapped = function watchGiftsUsfSuccess(...args) {
      if (typeof original === 'function') original.apply(this, args);
      scheduleSync();
    };
    wrapped.__watchGiftsWrapped = true;
    window._usfOnAddToCartSuccess = wrapped;
  };

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.watchGiftsSynced || event.detail?.baseEvent === 'watch-gifts:sync') return;
    scheduleSync(event.detail?.cart);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleSync();
  });

  document.addEventListener(
    'click',
    (event) => {
      holdCheckoutIfNeeded(event, checkoutTriggerFrom(event));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.hasAttribute(PROCEED_ATTR)) return;

      const submitter = event.submitter;
      const isCheckout = submitter?.getAttribute('name') === 'checkout' || Boolean(form.querySelector('[name="checkout"]'));
      if (!isCheckout || !syncIsPending()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      checkoutHoldCount += 1;

      scheduleSync()
        .finally(() => {
          checkoutHoldCount = Math.max(0, checkoutHoldCount - 1);
        })
        .then(() => {
          form.setAttribute(PROCEED_ATTR, '');
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
          else form.submit();
          form.removeAttribute(PROCEED_ATTR);
        });
    },
    true
  );

  window.fetch = function watchGiftsFetch(input, init) {
    if (!shouldSyncAfterFetch(input, init)) return nativeFetch(input, init);

    const replaceThemeResponse = /\/cart\/(change|update)\.js$/i.test(cartPathname(input));

    return nativeFetch(input, init).then(async (response) => {
      if (!response.ok) return response;

      const payload = await response.clone().json().catch(() => null);
      const cart = isFullCart(payload) ? payload : await getCart();
      const syncedCart = await runSync({
        cart,
        sections: sectionsFromInit(init) || bundledSections(),
        fromIntercept: replaceThemeResponse,
        silent: true
      });

      if (replaceThemeResponse && isFullCart(syncedCart)) {
        return new Response(JSON.stringify(syncedCart), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      }

      return response;
    });
  };

  wrapUsfSuccess();
  document.addEventListener('DOMContentLoaded', wrapUsfSuccess);
  window.addEventListener('load', wrapUsfSuccess);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
})();
